// src/components/NewTask.js
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { analyzeTasks } from '../features/tasks/taskSlice';
import { FaArrowLeft, FaSave } from 'react-icons/fa';

const NewTask = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [task, setTask] = useState({
        title: '',
        description: '',
        assignee: '',
        due_date: ''
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setTask(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!task.title.trim()) {
            alert('Please enter a task title');
            return;
        }

        setIsSubmitting(true);
        try {
            // Create task with ID
            const taskWithId = {
                ...task,
                id: Date.now().toString() // Simple ID generation
            };

            // Analyze the task
            await dispatch(analyzeTasks([taskWithId])).unwrap();

            alert('Task created and analyzed successfully!');
            navigate('/tasks');
        } catch (error) {
            alert('Failed to create task: ' + error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="new-task-container">
            <div className="new-task-header">
                <button className="back-button" onClick={() => navigate(-1)}>
                    <FaArrowLeft /> Back
                </button>
                <h1>Create New Task</h1>
            </div>

            <div className="new-task-content">
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="title">Task Title *</label>
                        <input
                            type="text"
                            id="title"
                            name="title"
                            value={task.title}
                            onChange={handleInputChange}
                            placeholder="Enter task title..."
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="description">Description</label>
                        <textarea
                            id="description"
                            name="description"
                            value={task.description}
                            onChange={handleInputChange}
                            placeholder="Describe the task in detail..."
                            rows="5"
                        />
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="assignee">Assignee</label>
                            <input
                                type="text"
                                id="assignee"
                                name="assignee"
                                value={task.assignee}
                                onChange={handleInputChange}
                                placeholder="Who is responsible?"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="due_date">Due Date</label>
                            <input
                                type="datetime-local"
                                id="due_date"
                                name="due_date"
                                value={task.due_date}
                                onChange={handleInputChange}
                            />
                        </div>
                    </div>

                    <div className="form-actions">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => navigate(-1)}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={isSubmitting}
                        >
                            <FaSave /> {isSubmitting ? 'Creating...' : 'Create & Analyze Task'}
                        </button>
                    </div>
                </form>
            </div>

            <style jsx>{`
                .new-task-container {
                    max-width: 800px;
                    margin: 0 auto;
                    padding: 20px;
                }

                .new-task-header {
                    display: flex;
                    align-items: center;
                    gap: 20px;
                    margin-bottom: 30px;
                    padding-bottom: 20px;
                    border-bottom: 2px solid #e9ecef;
                }

                .back-button {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    background: #6c757d;
                    color: white;
                    border: none;
                    padding: 10px 20px;
                    border-radius: 6px;
                    cursor: pointer;
                    transition: background 0.3s;
                }

                .back-button:hover {
                    background: #545b62;
                }

                .new-task-header h1 {
                    margin: 0;
                    color: #333;
                    font-size: 2rem;
                }

                .new-task-content {
                    background: white;
                    border-radius: 12px;
                    padding: 30px;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
                }

                .form-group {
                    margin-bottom: 25px;
                }

                .form-group label {
                    display: block;
                    margin-bottom: 8px;
                    font-weight: 600;
                    color: #495057;
                    font-size: 14px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .form-group input,
                .form-group textarea {
                    width: 100%;
                    padding: 12px 16px;
                    border: 2px solid #e9ecef;
                    border-radius: 8px;
                    font-size: 16px;
                    font-family: inherit;
                    transition: all 0.3s;
                    box-sizing: border-box;
                }

                .form-group input:focus,
                .form-group textarea:focus {
                    outline: none;
                    border-color: #667eea;
                    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
                }

                .form-group textarea {
                    resize: vertical;
                    min-height: 120px;
                }

                .form-row {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 20px;
                }

                .form-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 15px;
                    margin-top: 30px;
                    padding-top: 25px;
                    border-top: 2px solid #e9ecef;
                }

                .btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 12px 24px;
                    border: none;
                    border-radius: 6px;
                    cursor: pointer;
                    font-weight: 600;
                    transition: all 0.3s;
                    text-decoration: none;
                }

                .btn-secondary {
                    background: #6c757d;
                    color: white;
                }

                .btn-secondary:hover {
                    background: #545b62;
                }

                .btn-primary {
                    background: #667eea;
                    color: white;
                }

                .btn-primary:hover {
                    background: #5a6fd8;
                }

                .btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                @media (max-width: 768px) {
                    .new-task-header {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 15px;
                    }

                    .form-row {
                        grid-template-columns: 1fr;
                    }

                    .form-actions {
                        flex-direction: column;
                    }

                    .btn {
                        justify-content: center;
                    }
                }
            `}</style>
        </div>
    );
};

export default NewTask;